/**
 * sl-9qm.7: ThemeConfig Type Definitions + Zod Schema
 *
 * Defines the theme configuration structure for Custom Apps.
 * Stored in Y.js ViewConfig.settings.theme for collaborative editing.
 */
import { z } from 'zod';

/** Theme preset identifier */
export type ThemePreset = 'light' | 'dark' | 'brand' | 'minimal';

/** Spacing density */
export type SpacingDensity = 'compact' | 'normal' | 'relaxed';

/** Color palette for a theme */
export interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  accent: string;
  border: string;
}

/** Typography settings */
export interface ThemeTypography {
  fontFamily: string;
  headingFont?: string;
  baseFontSize: number;
  lineHeight: number;
  headingWeight: number;
}

/** Layout settings */
export interface ThemeLayout {
  maxWidth: number;
  borderRadius: number;
  spacing: SpacingDensity;
  headerHeight: number;
}

export type ThemeTokenCategory =
  | 'color'
  | 'font'
  | 'shadow'
  | 'spacing'
  | 'font-size'
  | 'font-weight'
  | 'border-radius';

export interface ThemeToken {
  name: string;
  value:
    | string
    | { type: 'value'; value: string }
    | { type: 'reference'; token: string };
  label?: string;
}

export type ThemeTokenSet = Record<ThemeTokenCategory, ThemeToken[]>;

/** Full theme configuration */
export interface ThemeConfig {
  preset: ThemePreset;
  colors: ThemeColors;
  typography: ThemeTypography;
  layout: ThemeLayout;
  tokens?: Partial<ThemeTokenSet>;
  customCss?: string;
}

// ─── Zod Schemas ──────────────────────────────────────────────────────

export const themeColorsSchema = z.object({
  primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  background: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  surface: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  text: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  textSecondary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  border: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});

export const themeTypographySchema = z.object({
  fontFamily: z.string().min(1),
  headingFont: z.string().optional(),
  baseFontSize: z.number().min(10).max(24),
  lineHeight: z.number().min(1).max(2.5),
  headingWeight: z.number().min(100).max(900),
});

export const themeLayoutSchema = z.object({
  maxWidth: z.number().min(0).max(2400),
  borderRadius: z.number().min(0).max(24),
  spacing: z.enum(['compact', 'normal', 'relaxed']),
  headerHeight: z.number().min(0).max(120),
});

export const themeTokenCategorySchema = z.enum([
  'color',
  'font',
  'shadow',
  'spacing',
  'font-size',
  'font-weight',
  'border-radius',
]);

export const themeTokenSchema = z.object({
  name: z
    .string()
    .min(1)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  value: z.union([
    z.string().min(1),
    z.object({ type: z.literal('value'), value: z.string().min(1) }),
    z.object({ type: z.literal('reference'), token: z.string().min(1) }),
  ]),
  label: z.string().min(1).optional(),
});

export const themeTokenSetSchema = z.object({
  color: z.array(themeTokenSchema).default([]),
  font: z.array(themeTokenSchema).default([]),
  shadow: z.array(themeTokenSchema).default([]),
  spacing: z.array(themeTokenSchema).default([]),
  'font-size': z.array(themeTokenSchema).default([]),
  'font-weight': z.array(themeTokenSchema).default([]),
  'border-radius': z.array(themeTokenSchema).default([]),
});

export const themeConfigSchema = z.object({
  preset: z.enum(['light', 'dark', 'brand', 'minimal']),
  colors: themeColorsSchema,
  typography: themeTypographySchema,
  layout: themeLayoutSchema,
  tokens: themeTokenSetSchema.partial().optional(),
  customCss: z.string().optional(),
});
